<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('locations') }}</h2>
    </div>

    <!-- Search Input -->
    <div class="mb-4">
      <input
          v-model="query"
          @input="searchLocations"
          class="form-control"
          type="text"
          :placeholder="$t('search_locations')"
      />
    </div>

    <!-- Add Location Button -->
    <div class="mb-4">
      <button v-if="hasPermission('users.add_location')" class="btn btn-primary" @click="openModal('add')">
        <i class="bi bi-plus-circle"></i> {{ $t('add_location') }}
      </button>
    </div>

    <!-- Pagination Buttons -->
    <div class="pagination-controls">
      <button
          class="btn pagination-btn"
          @click="goToPreviousPage()"
          v-if="showPreviousButton"
      >
        <i class="bi bi-arrow-left"></i>
        {{ $t('previous') }}
      </button>
      <button
          class="btn pagination-btn"
          @click="goToNextPage()"
          v-if="showNextButton"
      >
        {{ $t('next') }}
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>

    <!-- Location List -->
    <div class="columns is-multiline">
      <div class="column is-12">
        <template v-if="locations.length">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>{{ $t('id') }}</th>
              <th>{{ $t('name') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="location in locations" :key="location.id">
              <td>{{ location.id }}</td>
              <td>{{ location.name }}</td>
              <td>
                <!-- Edit button -->
                <button v-if="hasPermission('users.change_location')" class="btn btn-primary btn-sm"
                        @click="openModal('edit', location)">
                  <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
                </button>
                <!-- Delete button -->
                <button v-if="hasPermission('users.delete_location')" @click="openConfirmDelete(location.id)"
                        class="btn btn-danger btn-sm">
                  <i class="bi bi-trash"></i> {{ $t('delete') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>

        <!-- No locations available -->
        <div v-else>
          <p>{{ $t('no_locations_found') }}</p>
        </div>
      </div>
    </div>

    <!-- Bootstrap Modal for Add/Edit Location -->
    <div class="modal fade" id="locationModal" tabindex="-1" aria-labelledby="locationModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="locationModalLabel">{{
                isEditMode ? $t('edit_location') : $t('add_location')
              }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Add/Edit Form -->
            <form @submit.prevent="submitLocation">
              <div class="mb-3">
                <label for="locationName" class="form-label">{{ $t('location_name') }}</label>
                <input v-model="locationForm.name" type="text" class="form-control" id="locationName" required/>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="submitLocation">
              {{ isEditMode ? $t('update') : $t('add') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirm_delete') }}</h3>
        <p>{{ $t('confirm_delete_location') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteLocation">{{ $t('confirm') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'LocationList',
  data() {
    return {
      locations: [],
      query: '', // Search query
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      successMessage: '',
      errorMessage: '',
      isEditMode: false,
      locationForm: {
        id: null,
        name: ''
      },
      modalInstance: null,
      showConfirm: false, // Show custom confirm modal
      locationIdToDelete: null // Store the location ID to delete
    };
  },
  mounted() {
    this.getLocations();
    this.$nextTick(() => {
      const modalElement = document.getElementById('locationModal');
      if (modalElement) {
        this.modalInstance = new bootstrap.Modal(modalElement);
      }
    });
  },
  computed: {
    store: () => useMainStore(),
    hasPermission() {
      return (permission) => {
        const userPermissions = this.store.user.permissions || [];
        return userPermissions.includes(permission);
      };
    }
  },

  methods: {
    openModal(mode, location = null) {
      if (mode === 'add') {
        this.isEditMode = false;
        this.locationForm = {id: null, name: ''};
      } else if (mode === 'edit') {
        this.isEditMode = true;
        this.locationForm = {...location};
      }
      this.modalInstance.show();
    },
    closeModal() {
      this.modalInstance.hide();
    },
    async submitLocation() {
      try {
        if (this.isEditMode) {
          await axios.put(`/api/v1/admin/locations/${this.locationForm.id}/`, this.locationForm);
          this.successMessage = 'Location updated successfully';
        } else {
          await axios.post(`/api/v1/admin/locations/`, this.locationForm);
          this.successMessage = 'Location added successfully';
        }
        this.getLocations();
      } catch (error) {
        this.errorMessage = 'Failed to save location';
      } finally {
        this.closeModal();
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    openConfirmDelete(locationId) {
      this.locationIdToDelete = locationId;
      this.showConfirm = true;
    },
    async deleteLocation() {
      try {
        await axios.delete(`/api/v1/admin/locations/${this.locationIdToDelete}/`);
        this.successMessage = 'Location deleted successfully';
        this.getLocations();
      } catch (error) {
        this.errorMessage = 'Failed to delete location';
      } finally {
        this.showConfirm = false;
        setTimeout(() => {
          this.successMessage = '';
          this.errorMessage = '';
        }, 3000);
      }
    },
    cancelDelete() {
      this.showConfirm = false;
    },
    async getLocations() {
      this.showNextButton = false;
      this.showPreviousButton = false;
      try {
        const response = await axios.get(`/api/v1/admin/locations/?page=${this.currentPage}&search=${this.query}`);
        this.locations = response.data.results;
        this.showNextButton = Boolean(response.data.next);
        this.showPreviousButton = Boolean(response.data.previous);
      } catch (error) {
        this.errorMessage = 'Failed to fetch locations';
        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
      }
    },
    searchLocations() {
      this.currentPage = 1;
      this.getLocations();
    },
    goToNextPage() {
      this.currentPage += 1;
      this.getLocations();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getLocations();
    }
  }
};
</script>
